import { alertController, modalController, toastController } from '@ionic/core';
import { Component, Event, EventEmitter, Fragment, h, Host, Prop, State, Watch } from '@stencil/core';
import {
  Constants,
  DateUtils,
  FileDoc,
  FileType,
  PersonDoc,
  ProjectDoc,
  SubmittalDoc,
  SubmittalFeedItemDoc,
  SubmittalRevision,
  TagData,
  TaskDoc,
  TaskRelationship,
  Workflow,
  WorkflowState,
} from 'common';
import { PeopleService } from 'src/global/service/PeopleService';
import { ScheduleService } from 'src/global/service/ScheduleService';
import { DateSelector } from 'src/global/ui/date-selector';
import { SpecificationSectionSelector } from '../../components/concourse-documents/specification-section-selector';
import { RequestAccess } from '../../components/concourse-login/share-link/request-access';
import { ConcoursePersonSelector } from '../../components/concourse-person-selector/concourse-person-selector';
import { TaskSelector } from '../../components/concourse-schedule/task-selector';
import { Calendar } from '../../components/concourse-schedule/model/Calendar';
import { SubmittalUtils } from '..';
import { cloneDeep } from 'lodash';
import { ConcourseTagSelector } from '../../components/concourse-tag-selector/concourse-tag-selector';
import { ChangeEvent, ConcourseDb, DbListener, TaskInfoModal, TimelineUtils } from '../../global';
import { User } from '../../global/models';
import { CostCodeSelector } from '../../contracts/components/cost-code-selector';
import { SubmittalService } from '../services/SubmittalService';
import { ProjectService } from 'src/projects';
import { SubmittalPackageSelector } from './submittal-package-selector';
import { SubmittalTypeSelector } from './submittal-type-selector';

@Component({
  tag: 'submittal-edit-modal',
})
export class SubmittalEditModal {
  static async present(projectId: string, submittal: SubmittalDoc, sidepanel: boolean = false, testid: string = 'submittal-edit-modal'): Promise<SubmittalDoc | null> {
    const modal = await modalController.create({
      component: 'submittal-edit-modal',
      componentProps: { projectId, original: submittal, testid },
      backdropDismiss: false,
      cssClass: sidepanel ? 'modal-sidepanel' : 'modal-height-auto',
    });
    return new Promise(async resolve => {
      modal.addEventListener('saved', (e: CustomEvent<SubmittalDoc>) => {
        modal.dismiss();
        resolve(e.detail);
      });
      modal.addEventListener('dismiss', () => {
        modal.dismiss();
        resolve(null);
      });
      await modal.present();
    });
  }

  private people: PersonDoc[] = [];

  @Prop() projectId: string;
  @Prop() original: SubmittalDoc;
  @Prop() testid: string;

  @State() project: ProjectDoc = null;
  @State() submittal: SubmittalDoc = null;
  @State() revisionFeed: SubmittalFeedItemDoc[] = [];
  @State() calendar: Calendar = null;
  @State() scheduleTasks: TaskDoc[] = [];
  @State() workflows: Workflow[];
  @State() uploads: File[] = [];
  @State() isDragging: boolean = false;
  @State() error: string = '';
  @State() isSaving: boolean = false;
  @State() hideModal: boolean = false;

  @Event() saved: EventEmitter<SubmittalDoc>;
  @Event() dismiss: EventEmitter<void>;

  get user(): User {
    return globalThis.user;
  }

  get isMobile(): boolean {
    return globalThis.isMobile;
  }

  get revision(): SubmittalRevision {
    return SubmittalUtils.getLatestRevision(this.submittal);
  }

  set revision(revision: SubmittalRevision) {
    SubmittalUtils.setLatestRevision(this.submittal, revision);
  }

  get states(): WorkflowState[] {
    return this.revision.states;
  }

  get isDraft(): boolean {
    return SubmittalUtils.isDraft(this.submittal);
  }

  get filesRecievedPerson(): PersonDoc {
    return this.people.find(person => this.submittal.draft_files_recieved_from === person._id);
  }

  async componentWillLoad() {
    this.people = await PeopleService.getPersonDocs(this.projectId);
    this.project = await ProjectService.getProjectDoc(this.projectId);
    this.calendar = await ScheduleService.getCalendarOrDefault(this.projectId, this.project.default_calendar_unique_id);

    const workflows = this.project.submittals_workflows || [];
    workflows.unshift(SubmittalUtils.DEFAULT_SUBMITTAL_WORKFLOW);
    this.workflows = cloneDeep(workflows);

    await this.watchOriginal();
  }

  @Watch('original')
  async watchOriginal() {
    if (!this.original) return;
    // Deep copy to avoid mutating the original until save
    this.submittal = JSON.parse(JSON.stringify(this.original));

    if (this.isDraft) {
      this.syncDueDateFromStates();
    } else {
      this.loadRevisionFeed();
    }

    if (this.user.getSchedulePermissions(this.projectId).canRead && this.submittal.successors && this.submittal.successors.length) {
      this.loadScheduleTasks();
    }
  }

  async loadRevisionFeed() {
    const db = await SubmittalService.getFeedDb(this.projectId, this.submittal._id);
    db.addListeners(this.feedDbListener);
    db.syncRemote();
  }

  private feedDbListener: DbListener = {
    onComplete: (docs: SubmittalFeedItemDoc[]) => {
      this.revisionFeed = docs.filter(doc => doc.revision === this.revision.num);
    },
    onChange: (e: ChangeEvent) => {
      this.revisionFeed = e.docs.filter(doc => doc.revision === this.revision.num);
    },
  };

  private tasksDb: ConcourseDb;
  loadScheduleTasks() {
    this.tasksDb = ScheduleService.getTasksDb(this.projectId);
    this.tasksDb.addListener('complete', this.handleTasksComplete.bind(this));
  }

  private handleTasksComplete(docs: TaskDoc[]) {
    this.scheduleTasks = docs;
  }

  disconnectedCallback() {
    if (this.tasksDb) {
      this.tasksDb.removeListener('complete', this.handleTasksComplete.bind(this));
    }
  }

  async showTaskSelector() {
    if (!this.user.getSchedulePermissions(this.projectId).canRead) {
      RequestAccess.presentModal(this.projectId, 'Read Schedules', `p-${this.projectId}-schedules`);
      return;
    }
    this.hideModal = true;
    const task = await TaskSelector.presentModal(this.projectId);
    if (task) {
      const successors = this.submittal.successors || [];
      this.submittal = {
        ...this.submittal,
        successors: [...successors, { task_unique_id: task.unique_id, type: 'FS', lag: 0 }],
      };

      // If the tasks were not loaded on componentWillLoad, we load them now.
      if (!this.scheduleTasks || this.scheduleTasks.length == 0) {
        this.loadScheduleTasks();
      }
    }
    this.hideModal = false;
  }

  async removeDependency(relationship: TaskRelationship) {
    this.submittal = {
      ...this.submittal,
      successors: this.submittal.successors.filter(c => c.task_unique_id != relationship.task_unique_id),
    };
  }

  onWorkflowChange(workflow: Workflow) {
    this.revision = { ...this.revision, states: cloneDeep(workflow.states) };
    this.syncDueDateFromStates();
  }

  private syncDueDateFromStates() {
    const dueDate = TimelineUtils.getWorkflowEndDueDate(this.states, 0, DateUtils.currentDate, this.calendar);
    this.submittal = { ...this.submittal, due_date: DateUtils.toIso(dueDate) };
  }

  onRevisionChange(revision: SubmittalRevision) {
    this.revision = revision;
    this.syncDueDateFromStates();
  }

  async showSpecficationSelector() {
    this.hideModal = true;
    const section = await SpecificationSectionSelector.present(this.projectId);
    if (section) {
      this.submittal = { ...this.submittal, spec_section: section };
    }
    this.hideModal = false;
  }
  async showDateSelector() {
    const date = await DateSelector.present('Select Due Date', this.submittal.due_date);
    if (date) {
      this.submittal = { ...this.submittal, due_date: date };
    }
  }

  async showCostCodeSelector() {
    this.hideModal = true;
    const costCode = await CostCodeSelector.presentWithPromise(this.projectId);
    if (costCode) {
      this.submittal = { ...this.submittal, cost_code: costCode };
    }
    this.hideModal = false;
  }

  async showTypeSelector() {
    this.hideModal = true;
    const type = await SubmittalTypeSelector.present(this.projectId, this.submittal.type);
    if (type !== undefined) {
      this.submittal = { ...this.submittal, type: type || undefined };
    }
    this.hideModal = false;
  }

  async showPackageSelector() {
    this.hideModal = true;
    const pkg = await SubmittalPackageSelector.present(this.projectId, this.submittal.package);
    if (pkg !== undefined) {
      this.submittal = { ...this.submittal, package: pkg || undefined };
    }
    this.hideModal = false;
  }

  async addDistributionPerson() {
    const people = await ConcoursePersonSelector.present({
      projectId: this.projectId,
      feature: 'submittals',
      featureId: this.submittal._id,
      peopleToOmitIds: this.submittal.distribution,
      modalTitle: 'Select people to distribute the submittal to',
      multiSelect: true,
    });
    if (people && people.length > 0) {
      let distribution = this.submittal.distribution || [];
      for (const person of people) {
        distribution = [...distribution, person._id];
        // If the new person was added, we don't wait for the people db to sync
        // although its possilbe that the sync would have completed very soon
        if (this.people.findIndex(p => p._id === person._id) < 0) {
          this.people.push(person);
        }
      }
      this.submittal = { ...this.submittal, distribution };
    }
  }

  async removeDistributionPerson(personId: string) {
    this.submittal = {
      ...this.submittal,
      distribution: this.submittal.distribution.filter(id => id !== personId),
    };
  }

  async onUploadSelected(files: File[]) {
    this.uploads = [...this.uploads, ...files];
  }

  parseDataTransfer(transfer: DataTransfer): File[] {
    if (transfer.items) return Array.from(transfer.items).map((item: DataTransferItem) => item.getAsFile());
    return Array.from(transfer.files);
  }

  async onUploaded(file: FileDoc) {
    console.log(`submittal-create-modal onUploaded ${file._id} name: ${file.name} `);

    this.uploads = this.uploads.filter(f => f.name != file.name);

    const files = this.submittal.draft_files || [];
    this.submittal = { ...this.submittal, draft_files: [...files, file] };
    SubmittalService.updateSubmittal(this.projectId, this.submittal as SubmittalDoc);
  }

  removeFileUpload(indexToremove: string) {
    this.uploads = this.uploads.filter((_, index) => index !== parseInt(indexToremove));
  }

  removeFile(file: FileDoc) {
    this.submittal = { ...this.submittal, draft_files: this.submittal.draft_files.filter(f => f._id !== file._id) };
    SubmittalService.updateSubmittal(this.projectId, this.submittal as SubmittalDoc);
  }

  async showFilesReceivedDateSelector(event: CustomEvent) {
    this.submittal = { ...this.submittal, draft_files_received_date: event.detail.detail.value };
  }

  async showFilesRecievedFromSelector() {
    const peopleToOmitIds: string[] = this.submittal.draft_files_recieved_from ? [this.submittal.draft_files_recieved_from] : [];
    const people = await ConcoursePersonSelector.present({ projectId: this.projectId, feature: 'submittals', featureId: this.submittal._id, peopleToOmitIds, multiSelect: false });
    if (people && people.length > 0) {
      this.submittal = { ...this.submittal, draft_files_recieved_from: people[0]._id };
      SubmittalService.updateSubmittal(this.projectId, this.submittal as SubmittalDoc);
    }
  }

  async removeFilesReceivedFrom() {
    this.submittal = { ...this.submittal, draft_files_recieved_from: undefined };
  }

  async deleteDraft() {
    // this submittal has never been saved, so we can just delete it
    if (!this.submittal._rev) {
      this.saved.emit({ ...(this.submittal as SubmittalDoc), _deleted: true, deleted_by: this.user.id, deleted_date: new Date().toISOString() });
    } else {
      const alert = await alertController.create({
        header: 'Delete Draft',
        message: 'Are you sure you want to delete this draft?',
        buttons: ['Cancel', { text: 'Delete', role: 'delete', handler: () => this.deleteDraftConfirmed() }],
      });
      await alert.present();
    }
  }

  async deleteDraftConfirmed() {
    try {
      const deletedSubmittal: SubmittalDoc = {
        ...(this.submittal as SubmittalDoc),
        deleted_by: this.user.id,
        deleted_date: new Date().toISOString(),
        updated_by: this.user.id,
        updated_date: new Date().toISOString(),
      };
      await SubmittalService.updateSubmittal(this.projectId, deletedSubmittal);
      this.saved.emit(deletedSubmittal);
    } catch (error) {
      console.error('Error deleting draft:', error);
      const alert = await alertController.create({
        header: 'Error',
        message: `Failed to delete draft: ${error.message}`,
        buttons: ['OK'],
      });
      await alert.present();
    }
  }

  async saveSubmittal() {
    this.isSaving = true;
    try {
      const savedSubmittal = await SubmittalService.updateSubmittal(this.projectId, this.submittal as SubmittalDoc);
      this.saved.emit(savedSubmittal);
    } catch (error) {
      console.error('Error saving submittal:', error);
      // Show error to user
      const alert = await alertController.create({
        header: 'Error',
        message: `Failed to save submittal: ${error.message}`,
        buttons: ['OK'],
      });
      await alert.present();
    } finally {
      this.isSaving = false;
    }
  }

  async createSubmittal() {
    if (!this.states || !this.states.length) {
      this.showErrorToast('Please select a workflow');
      return;
    }
    if (!this.submittal.due_date) {
      this.showErrorToast('Please select a due date');
      return;
    }

    if (this.submittal.draft_files?.length > 0 && !this.submittal.draft_files_received_date) {
      this.showErrorToast('Please select a date for the files received');
      return;
    }
    if (this.submittal.draft_files?.length > 0 && this.submittal.draft_files_recieved_from == null) {
      this.showErrorToast('Please specify who the files were received from');
      return;
    }
    if (!this.submittal.title && !this.submittal.description) {
      this.showErrorToast('Title and description are required');
      return;
    }
    this.isSaving = true;
    try {
      const completedStateId = this.submittal.draft_files?.length > 0 ? this.states[1].id : this.states[0].id;
      const completedStateIdx = this.states.findIndex(s => s.id === completedStateId);

      const nextState = this.states[completedStateIdx + 1];

      if (nextState && nextState.dueBy.length === 0) {
        const people = await ConcoursePersonSelector.present({
          projectId: this.projectId,
          feature: 'submittals',
          featureId: this.submittal._id,
          modalTitle: 'Please select the person who will be assigned to the submittal',
          needsWrite: true,
        });
        if (people && people.length > 0) {
          nextState.dueBy = people.map(p => p._id);
        } else {
          this.showErrorToast('You must select the person who will be assigned to the submittal');
          return;
        }
      }

      // the creator takes ownership for all prior states and the completed state.
      for (var i = 0; i <= completedStateIdx; i++) {
        this.states[i].dueBy = [this.user.id];
      }

      // this is no longer in draft
      this.submittal.created_by = this.user.id;
      this.submittal.created_date = new Date().toISOString();

      const updatedSubmittal = await SubmittalService.completeState(this.projectId, this.submittal);

      this.saved.emit(updatedSubmittal);
    } catch (error) {
      this.showErrorToast(`Failed to assign submittal: ${error.message}`);
      console.error('Error creating submittal:', error);
    } finally {
      this.isSaving = false;
    }
  }

  private async showErrorToast(error: string) {
    const toast = await toastController.create({
      message: error,
      duration: 5000,
      color: 'danger',
      position: 'bottom',
    });
    await toast.present();
  }

  render() {
    if (!this.submittal || this.hideModal || !this.submittal?._id) return <div class="concourse-modal modal-height-auto"></div>;

    const submittalStateIds = this.states?.map(s => s.id).join(',') || '';
    const workflow = this.workflows.find(w => w.states.map(s => s.id).join(',') == submittalStateIds);
    const modalTitle = this.submittal._rev ? (this.isDraft ? 'Edit Draft' : 'Edit Submittal') : 'Create Submittal';

    const finalState = this.states[this.states.length - 1];
    const finalStatePeople = finalState?.dueBy?.map(p => PeopleService.findPerson(this.people, p)).filter(p => p !== null) || [];
    return (
      <Host class="concourse-modal" data-testid={this.testid}>
        <modal-header modalTitle={modalTitle} onDismiss={() => this.dismiss.emit()}>
          {this.submittal._rev && this.isDraft && (
            <ion-button fill="clear" onClick={() => this.deleteDraft()} data-testid="submittal-edit-delete-draft-button">
              <ion-icon slot="icon-only" name="trash-outline"></ion-icon>
            </ion-button>
          )}
        </modal-header>
        <div class="modal-content cc-scrollbars">
          <ion-grid>
            <ion-row>
              <ion-col size="12">
                <ion-input
                  label="Title"
                  label-placement="stacked"
                  value={this.submittal.title}
                  onIonInput={e => (this.submittal.title = (e.target as HTMLInputElement).value)}
                  id="submittal-title-input"
                  required
                />
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col size="6">
                <ion-input
                  label="Number"
                  label-placement="stacked"
                  value={this.submittal.code ?? ''}
                  onIonInput={e => (this.submittal.code = (e.target as HTMLInputElement).value)}
                  id="submittal-num-input"
                  required
                />
              </ion-col>
              <ion-col size="6">
                <date-picker-input
                  selectedDate={this.submittal.due_date}
                  inputProps={{
                    clearInput: true,
                    labelPlacement: 'stacked',
                    label: 'Due Date',
                    name: 'due_date',
                    value: this.submittal.due_date ? new Date(this.submittal.due_date).toLocaleDateString() : '',
                  }}
                  onIonChange={event => {
                    this.submittal = { ...this.submittal, due_date: event.detail.detail.value };
                  }}
                ></date-picker-input>
              </ion-col>
            </ion-row>
            {/* Can only change workflow in draft mode */}
            {this.isDraft && (
              <ion-row>
                <ion-col size="12">
                  <select-input
                    data-testid="submittal-workflow-selector"
                    inputProps={{
                      label: 'Workflow',
                      labelPlacement: 'stacked',
                      placeholder: 'Select workflow',
                      value: workflow?.name || '',
                    }}
                    selectProps={{
                      required: true,
                      value: workflow,
                    }}
                    onSelectChange={event => this.onWorkflowChange(event.detail)}
                  >
                    {this.workflows.map(workflow => (
                      <ion-select-option value={workflow}>{workflow.name}</ion-select-option>
                    ))}
                  </select-input>
                </ion-col>
                <ion-col size="12" class="ion-padding-horizontal">
                  {this.states?.length > 0 && (
                    <submittal-timeline
                      projectId={this.projectId}
                      submittal={this.submittal}
                      feed={this.revisionFeed}
                      people={this.people}
                      calendar={this.calendar}
                      canWrite={true}
                      onRevisionChange={e => this.onRevisionChange(e.detail)}
                    />
                  )}
                </ion-col>
              </ion-row>
            )}
            <ion-row>
              <ion-col>
                <ion-textarea
                  label="Description"
                  label-placement="stacked"
                  value={this.submittal.description}
                  auto-grow="true"
                  onIonInput={e => (this.submittal.description = (e.target as HTMLTextAreaElement).value)}
                  id="submittal-description-input"
                />
              </ion-col>
            </ion-row>
            {/* only show attachments in draft mode */}
            {this.isDraft && (
              <ion-row>
                <ion-col>
                  <ion-label class="typography-body2-400" style={{ marginBottom: '6px', color: 'var(--background-color-step-950)' }}>
                    Attachments
                  </ion-label>
                  {(this.submittal.draft_files?.length > 0 || this.uploads.length > 0) && (
                    <Fragment>
                      {this.submittal.draft_files?.map(file => {
                        return <file-preview-card key={file._id} file={file} showDelete={true} onDelete={() => this.removeFile(file)} />;
                      })}
                      {this.uploads.map((file, index) => (
                        <file-uploader-instance
                          style={{ width: '100%' }}
                          data-testid="submittal-file-uploader-instance"
                          data-filename={file.name}
                          projectId={this.projectId}
                          fileType={FileType.DOCUMENT}
                          file={file}
                          fileRoles={SubmittalUtils.getReadRoles(this.projectId, this.submittal._id)}
                          fileId={index.toString()}
                          onUploaded={e => this.onUploaded(e.detail)}
                          onRemoveFileId={e => this.removeFileUpload(e.detail)}
                        />
                      ))}
                    </Fragment>
                  )}
                </ion-col>
              </ion-row>
            )}

            {this.isDraft && (
              <Fragment>
                {(this.submittal.draft_files?.length > 0 || this.uploads.length > 0) && [
                  <ion-row>
                    <ion-col size="6">
                      <ion-label class="typography-body2-400" style={{ marginBottom: '6px', color: 'var(--background-color-step-950)' }}>
                        Received From
                      </ion-label>
                      <br />
                      {this.filesRecievedPerson ? (
                        <person-chip person={this.filesRecievedPerson} removable={true} onRemove={() => this.removeFilesReceivedFrom()} />
                      ) : (
                        <ion-button fill="clear" shape="round" size="small" onClick={() => this.showFilesRecievedFromSelector()}>
                          <ion-icon slot="start" name="add-outline" color="primary"></ion-icon>
                          Add
                        </ion-button>
                      )}
                    </ion-col>
                    <ion-col size="6">
                      <ion-label class="typography-body2-400" style={{ marginBottom: '6px', color: 'var(--background-color-step-950)' }}>
                        Recieved Date
                      </ion-label>
                      <br />

                      <date-picker-input
                        selectedDate={this.submittal.draft_files_received_date}
                        inputProps={{
                          clearInput: true,
                          name: 'files_received_date',
                          value: this.submittal.draft_files_received_date ? new Date(this.submittal.draft_files_received_date).toLocaleDateString() : '',
                        }}
                        onIonChange={event => this.showFilesReceivedDateSelector(event)}
                      ></date-picker-input>
                    </ion-col>
                  </ion-row>,
                  <ion-row size="12">
                    <ion-col size="12">
                      <ion-text color="warning">
                        * This submittal will be assigned {finalStatePeople.length > 0 && 'to'} {finalStatePeople.map(p => PeopleService.getFullName(p)).join(', ')} to '
                        {finalState.action}'.
                      </ion-text>
                    </ion-col>
                  </ion-row>,
                ]}
                <ion-row>
                  <ion-col size="12">
                    <concourse-file-uploader
                      style={{ width: '100%' }}
                      projectId={this.projectId}
                      multiple={true}
                      onFilesSelected={event => this.onUploadSelected(event.detail)}
                      data-testid="submittal-file-uploader"
                    />
                  </ion-col>
                </ion-row>
              </Fragment>
            )}

            <ion-row>
              <ion-col size="6">
                <div onClick={() => this.showTypeSelector()}>
                  <ion-input label="Type (optional)" label-placement="stacked" placeholder="Select type" value={this.submittal.type || ''} readonly>
                    <ion-icon slot="end" name="chevron-down-outline" class="input-icon" aria-hidden="true"></ion-icon>
                  </ion-input>
                </div>
              </ion-col>
              <ion-col size="6">
                <div onClick={() => this.showPackageSelector()}>
                  <ion-input label="Package (optional)" label-placement="stacked" placeholder="Select package" value={this.submittal.package?.title || ''} readonly>
                    <ion-icon slot="end" name="chevron-down-outline" class="input-icon" aria-hidden="true"></ion-icon>
                  </ion-input>
                </div>
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col size="6">
                <div onClick={() => this.showSpecficationSelector()}>
                  <ion-input
                    label="Specification Section (optional)"
                    label-placement="stacked"
                    placeholder="Select Spec Section"
                    value={this.submittal.spec_section ? `${this.submittal.spec_section.num} - ${this.submittal.spec_section.title}` : ''}
                    readonly
                  >
                    <ion-icon slot="end" name="chevron-down-outline" class="input-icon" aria-hidden="true"></ion-icon>
                  </ion-input>
                </div>
              </ion-col>
              <ion-col size="6">
                <div onClick={() => this.showCostCodeSelector()}>
                  <ion-input
                    label="Cost Code (optional)"
                    label-placement="stacked"
                    placeholder="Select Cost Code"
                    value={this.submittal.cost_code ? `${this.submittal.cost_code.code_number} - ${this.submittal.cost_code.division_name}` : ''}
                    readonly
                  >
                    <ion-icon slot="end" name="chevron-down-outline" class="input-icon" aria-hidden="true"></ion-icon>
                  </ion-input>
                </div>
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col size="12">
                <ion-label class="typography-body2-400" style={{ marginBottom: '6px', color: 'var(--background-color-step-950)' }}>
                  Affected Tasks (optional)
                </ion-label>
              </ion-col>
              <ion-col size="12" class="ion-display-flex ion-flex-wrap ion-align-items-center">
                {this.submittal.successors?.map(relationship => {
                  const task = this.scheduleTasks && this.scheduleTasks.length > 0 ? this.scheduleTasks.find(t => t.unique_id == relationship.task_unique_id) : null;
                  return (
                    <ion-chip
                      onClick={e => {
                        e.stopPropagation();
                        if (task) {
                          TaskInfoModal.presentModal(task);
                        }
                      }}
                    >
                      <ion-label>{task ? task.name : relationship.task_unique_id}</ion-label>
                      <ion-icon
                        name="close-circle"
                        onClick={e => {
                          e.stopPropagation();
                          this.removeDependency(relationship);
                        }}
                      ></ion-icon>
                    </ion-chip>
                  );
                })}
                <ion-button fill="clear" shape="round" size="small" onClick={() => this.showTaskSelector()}>
                  <ion-icon slot="start" name="add-outline" color="primary"></ion-icon>
                  Add
                </ion-button>
              </ion-col>
            </ion-row>
            <chip-list-section sectionTitle="Distribution (optional)" addClick={() => this.addDistributionPerson()}>
              {this.submittal.distribution?.map(personId => {
                const person = this.people.find(p => p._id === personId);
                return <person-chip person={person} removable={true} onRemove={() => this.removeDistributionPerson(personId)} />;
              })}
            </chip-list-section>
            <ion-row>
              <ion-col size="12">
                <ion-label class="typography-body2-400" style={{ marginBottom: '6px', color: 'var(--background-color-step-950)' }}>
                  Tags (optional)
                </ion-label>
              </ion-col>
              <ion-col size="12" class="ion-display-flex ion-flex-wrap ion-align-items-center">
                {this.submittal.tags?.map(tag => {
                  return (
                    <ion-chip>
                      <ion-label>{tag.name}</ion-label>
                      <ion-icon name="close-circle" onClick={() => this.removeTag(tag)}></ion-icon>
                    </ion-chip>
                  );
                })}
                <ion-button fill="clear" shape="round" size="small" onClick={() => this.showTagSelector()}>
                  <ion-icon slot="start" name="add-outline" color="primary"></ion-icon>
                  Add
                </ion-button>
              </ion-col>
            </ion-row>
          </ion-grid>
        </div>
        <ion-footer>
          <ion-toolbar>
            {this.isDraft ? (
              [
                <ion-button slot="start" color="light" fill="clear" expand={this.isMobile ? 'block' : undefined} onClick={() => this.saveSubmittal()}>
                  Save Draft
                </ion-button>,
                <ion-button slot="end" fill="solid" shape="round" disabled={this.isSaving} onClick={() => this.createSubmittal()} data-testid="submittal-edit__save-button">
                  {this.isSaving ? <ion-spinner name="crescent" size="small" slot="start"></ion-spinner> : null}
                  {this.isSaving ? 'Saving...' : 'Create Submittal'}
                </ion-button>,
              ]
            ) : (
              <ion-button
                color="primary"
                fill="solid"
                shape="round"
                slot="end"
                disabled={this.isSaving}
                onClick={() => this.saveSubmittal()}
                data-testid="submittal-edit__update-button"
              >
                {this.isSaving ? 'Saving Submittal...' : 'Save'}
                {this.isSaving ? <ion-spinner name="dots" size="small" slot="end"></ion-spinner> : null}
              </ion-button>
            )}
          </ion-toolbar>
        </ion-footer>
      </Host>
    );
  }

  async showTagSelector() {
    ConcourseTagSelector.presentModal(this.projectId, null, (tag: TagData) => this.addTag(tag));
  }

  async addTag(tag: TagData) {
    this.submittal = {
      ...this.submittal,
      tags: [...(this.submittal.tags || []), tag],
    };
  }

  async removeTag(tag: TagData) {
    this.submittal = {
      ...this.submittal,
      tags: this.submittal.tags.filter(t => t.name !== tag.name),
    };
  }
}
